/**
 * PresentationML slide parsing and surgical text replacement.
 *
 * A slide is a flat tree of positioned shapes, which makes it easier to render
 * faithfully than a word processor's reflowing text — the file says where
 * everything goes. What it does *not* always say is where a placeholder goes:
 * a title that inherits its box from the layout carries no `<a:xfrm>` of its
 * own, and a renderer that skipped the lookup would stack every such shape at
 * the origin. Resolving that inheritance is most of what this module does
 * beyond scanning.
 *
 * Editing follows the same rule as the DOCX side: each shape records the byte
 * span it occupies, and a text change rewrites only that span. Animations,
 * transitions, SmartArt, charts and embedded media are never parsed on the
 * save path, so they cannot be lost there (§13, §17, §24).
 */
import { escapeXml, unescapeXml } from '../ooxml/xml.js';

/** EMU per CSS pixel at 96 dpi. */
export const EMU_PER_PX = 9525;
/** Hundredths of a point → CSS pixels at 96 dpi. */
const SZ_TO_PX = 96 / 72 / 100;

export interface SlideRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface SlideRun {
  readonly text: string;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  /** CSS pixels, or null when the shape should use its inherited size. */
  readonly sizePx: number | null;
  readonly color: string | null;
  readonly href: string | null;
}

export interface SlideParagraph {
  readonly runs: readonly SlideRun[];
  readonly text: string;
  /** Indent level, 0–8. Drives the bullet inset. */
  readonly level: number;
  readonly alignment: 'left' | 'center' | 'right' | 'justify' | null;
  /** False when the paragraph explicitly turns bullets off (`<a:buNone/>`). */
  readonly bulleted: boolean;
}

export type SlideShape =
  | {
    readonly kind: 'text';
    readonly id: string;
    readonly name: string;
    readonly rect: SlideRect | null;
    readonly rotation: number;
    /** `title`, `body`, `ctrTitle`, … when the shape is a placeholder. */
    readonly placeholder: string | null;
    readonly paragraphs: readonly SlideParagraph[];
    readonly text: string;
    /**
     * The text this shape had in the file on disk.
     *
     * Typing something and then typing the original back must leave no pending
     * edit; comparing against the already-edited text would never notice that.
     */
    readonly originalText: string;
    /** Vertical anchoring inside the box: `t`, `ctr` or `b`. */
    readonly anchor: 'top' | 'middle' | 'bottom';
    /** False when rebuilding the text would lose a field or other markup. */
    readonly editable: boolean;
    readonly start: number;
    readonly end: number;
  }
  | {
    readonly kind: 'picture';
    readonly id: string;
    readonly name: string;
    readonly rect: SlideRect | null;
    readonly rotation: number;
    readonly relId: string;
    readonly part: string | null;
    readonly alt: string;
    readonly start: number;
    readonly end: number;
  }
  | {
    /** A table, chart, SmartArt or embedded object frame. */
    readonly kind: 'frame';
    readonly id: string;
    readonly name: string;
    readonly rect: SlideRect | null;
    readonly rotation: number;
    /** `table`, `chart`, `diagram` or `object` — what the frame holds. */
    readonly content: string;
    /** Cell text, row by row. Empty for anything that is not a table. */
    readonly rows: readonly (readonly string[])[];
    readonly start: number;
    readonly end: number;
  };

export interface ParsedSlide {
  readonly shapes: readonly SlideShape[];
  /** True when the shape budget stopped the scan (§33). */
  readonly truncated: boolean;
}

/* ------------------------------------------------------------------ *
 * scanning helpers
 * ------------------------------------------------------------------ */

function attr(tag: string, name: string): string | null {
  const found = new RegExp(`\\b${name.replace(':', '\\:')}="([^"]*)"`).exec(tag);
  return found ? unescapeXml(found[1]!) : null;
}

/** Offset just past the closing tag of the element starting at `open`. */
function endOfElement(xml: string, open: number, name: string): number {
  const openTag = `<${name}`;
  const closeTag = `</${name}>`;
  const tagEnd = xml.indexOf('>', open);
  if (tagEnd < 0) return xml.length;
  if (xml[tagEnd - 1] === '/') return tagEnd + 1;

  let depth = 1;
  let cursor = tagEnd + 1;
  while (depth > 0) {
    const nextOpen = xml.indexOf(openTag, cursor);
    const nextClose = xml.indexOf(closeTag, cursor);
    if (nextClose < 0) return xml.length;
    if (nextOpen >= 0 && nextOpen < nextClose) {
      const nextTagEnd = xml.indexOf('>', nextOpen);
      if (nextTagEnd < 0) return xml.length;
      const after = xml[nextOpen + openTag.length]!;
      if ((after === '>' || after === ' ' || after === '/') && xml[nextTagEnd - 1] !== '/') {
        depth += 1;
      }
      cursor = nextTagEnd + 1;
      continue;
    }
    depth -= 1;
    cursor = nextClose + closeTag.length;
  }
  return cursor;
}

/** Start tag position, skipping names that merely share a prefix. */
function indexOfElement(xml: string, tag: string, from: number, limit: number): number {
  let cursor = from;
  for (;;) {
    const found = xml.indexOf(tag, cursor);
    if (found < 0 || found >= limit) return -1;
    const after = xml[found + tag.length];
    if (after === '>' || after === ' ' || after === '/' || after === '\n' || after === '\r' || after === '\t') {
      return found;
    }
    cursor = found + tag.length;
  }
}

function parseRect(xml: string): SlideRect | null {
  // The shape's own transform, not one belonging to a nested shape.
  const xfrm = /<a:xfrm\b[^>]*>[\s\S]*?<\/a:xfrm>/.exec(xml)?.[0];
  if (!xfrm) return null;
  const off = /<a:off\b[^>]*\bx="(-?\d+)"[^>]*\by="(-?\d+)"/.exec(xfrm);
  const ext = /<a:ext\b[^>]*\bcx="(\d+)"[^>]*\bcy="(\d+)"/.exec(xfrm);
  if (!off || !ext) return null;
  return {
    x: Number(off[1]) / EMU_PER_PX,
    y: Number(off[2]) / EMU_PER_PX,
    width: Number(ext[1]) / EMU_PER_PX,
    height: Number(ext[2]) / EMU_PER_PX,
  };
}

function parseRotation(xml: string): number {
  // `rot` is in 60000ths of a degree, on the `<a:xfrm>` element.
  const rot = /<a:xfrm\b[^>]*\brot="(-?\d+)"/.exec(xml)?.[1];
  return rot ? Number(rot) / 60000 : 0;
}

/* ------------------------------------------------------------------ *
 * text bodies
 * ------------------------------------------------------------------ */

function parseRuns(paragraphXml: string, rels: ReadonlyMap<string, string>): SlideRun[] {
  const out: SlideRun[] = [];
  const RUN = /<a:(r|br|fld)\b(?:[^>]*?)(?:\/>|>([\s\S]*?)<\/a:\1>)/g;
  for (const match of paragraphXml.matchAll(RUN)) {
    const name = match[1]!;
    const body = match[2] ?? '';

    if (name === 'br') {
      out.push({ text: '\n', bold: false, italic: false, underline: false, sizePx: null, color: null, href: null });
      continue;
    }

    const text = /<a:t\b[^>]*>([\s\S]*?)<\/a:t>/.exec(body)?.[1];
    if (text === undefined) continue;

    const rPr = /<a:rPr\b([^>]*?)\s*(?:\/>|>([\s\S]*?)<\/a:rPr>)/.exec(body);
    const rPrAttrs = rPr?.[1] ?? '';
    const rPrBody = rPr?.[2] ?? '';
    const size = attr(rPrAttrs, 'sz');
    const linkId = /<a:hlinkClick\b[^>]*r:id="([^"]+)"/.exec(rPrBody)?.[1];

    out.push({
      text: unescapeXml(text),
      bold: attr(rPrAttrs, 'b') === '1',
      italic: attr(rPrAttrs, 'i') === '1',
      underline: (attr(rPrAttrs, 'u') ?? 'none') !== 'none',
      sizePx: size ? Number(size) * SZ_TO_PX : null,
      color: /<a:solidFill>[\s\S]*?<a:srgbClr\b[^>]*val="([0-9A-Fa-f]{6})"/.exec(rPrBody)?.[1] ?? null,
      href: linkId ? rels.get(linkId) ?? null : null,
    });
  }
  return out;
}

function parseParagraphs(txBody: string, rels: ReadonlyMap<string, string>): SlideParagraph[] {
  const out: SlideParagraph[] = [];
  for (const match of txBody.matchAll(/<a:p\b(?:[^>]*?)(?:\/>|>([\s\S]*?)<\/a:p>)/g)) {
    const body = match[1] ?? '';
    const pPr = /<a:pPr\b([^>]*?)\s*(?:\/>|>([\s\S]*?)<\/a:pPr>)/.exec(body);
    const pPrAttrs = pPr?.[1] ?? '';
    const pPrBody = pPr?.[2] ?? '';
    const alignRaw = attr(pPrAttrs, 'algn');

    const runs = parseRuns(body, rels);
    out.push({
      runs,
      text: runs.map((r) => r.text).join(''),
      level: Number(attr(pPrAttrs, 'lvl') ?? 0),
      alignment: alignRaw === 'ctr' ? 'center'
        : alignRaw === 'r' ? 'right'
          : alignRaw === 'just' ? 'justify'
            : alignRaw === 'l' ? 'left' : null,
      bulleted: !/<a:buNone\b/.test(pPrBody),
    });
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * shapes
 * ------------------------------------------------------------------ */

export interface SlideParseOptions {
  /** Relationship id → part name or external URL, from the slide's rels. */
  readonly rels: ReadonlyMap<string, string>;
  /** Placeholder key → box, inherited from the layout and master. */
  readonly placeholderRects: ReadonlyMap<string, SlideRect>;
  readonly maxShapes: number;
}

/**
 * Key a placeholder by type and index.
 *
 * Both matter: a layout can hold two `body` placeholders distinguished only by
 * `idx`, and a slide's title carries a type with no index at all. Trying one
 * and then the other is what the lookup does, so the key format has to make
 * both forms constructible.
 */
export function placeholderKey(type: string | null, idx: string | null): string {
  return `${type ?? 'body'}#${idx ?? ''}`;
}

function resolvePlaceholderRect(
  type: string | null, idx: string | null, table: ReadonlyMap<string, SlideRect>,
): SlideRect | null {
  return table.get(placeholderKey(type, idx))
    ?? table.get(placeholderKey(type, null))
    ?? (type === 'ctrTitle' || type === 'subTitle'
      // A layout may describe the same role under the plainer name.
      ? table.get(placeholderKey(type === 'ctrTitle' ? 'title' : 'body', null)) ?? null
      : null);
}

function parseShape(
  xml: string, start: number, end: number, options: SlideParseOptions,
): SlideShape | null {
  const source = xml.slice(start, end);
  const cNvPr = /<p:cNvPr\b([^>]*)/.exec(source)?.[1] ?? '';
  const id = attr(cNvPr, 'id') ?? '';
  const name = attr(cNvPr, 'name') ?? '';

  const phTag = /<p:ph\b([^>]*)/.exec(source)?.[1] ?? null;
  const phType = phTag ? attr(phTag, 'type') : null;
  const phIdx = phTag ? attr(phTag, 'idx') : null;

  const ownRect = parseRect(source);
  const rect = ownRect
    ?? (phTag ? resolvePlaceholderRect(phType, phIdx, options.placeholderRects) : null);
  const rotation = parseRotation(source);

  if (source.startsWith('<p:pic')) {
    const relId = /r:embed="([^"]+)"/.exec(source)?.[1] ?? '';
    return {
      kind: 'picture', id, name, rect, rotation, relId,
      part: relId ? options.rels.get(relId) ?? null : null,
      alt: attr(cNvPr, 'descr') ?? name,
      start, end,
    };
  }

  if (source.startsWith('<p:graphicFrame')) {
    const uri = /<a:graphicData\b[^>]*uri="([^"]*)"/.exec(source)?.[1] ?? '';
    const content = uri.includes('/table') ? 'table'
      : uri.includes('/chart') ? 'chart'
        : uri.includes('/diagram') ? 'diagram' : 'object';

    const rows: string[][] = [];
    if (content === 'table') {
      for (const row of source.matchAll(/<a:tr\b(?:[^>]*?)>([\s\S]*?)<\/a:tr>/g)) {
        const cells: string[] = [];
        for (const cell of row[1]!.matchAll(/<a:tc\b(?:[^>]*?)>([\s\S]*?)<\/a:tc>/g)) {
          cells.push([...cell[1]!.matchAll(/<a:t\b[^>]*>([\s\S]*?)<\/a:t>/g)]
            .map((t) => unescapeXml(t[1]!)).join(''));
        }
        rows.push(cells);
      }
    }
    return { kind: 'frame', id, name, rect, rotation, content, rows, start, end };
  }

  const txBody = /<p:txBody\b[^>]*>([\s\S]*?)<\/p:txBody>/.exec(source)?.[1];
  if (txBody === undefined) return null;

  const paragraphs = parseParagraphs(txBody, options.rels);
  const text = paragraphs.map((p) => p.text).join('\n');
  const anchorRaw = /<a:bodyPr\b[^>]*\banchor="([^"]+)"/.exec(txBody)?.[1];

  return {
    kind: 'text',
    id, name, rect, rotation,
    placeholder: phType ?? (phTag ? 'body' : null),
    paragraphs,
    text,
    originalText: text,
    anchor: anchorRaw === 'ctr' ? 'middle' : anchorRaw === 'b' ? 'bottom' : 'top',
    // A field (slide number, date) is generated at display time; rebuilding
    // the body from plain text would freeze it into a literal string.
    editable: !/<a:fld\b/.test(txBody),
    start, end,
  };
}

export function parseSlide(xml: string, options: SlideParseOptions): ParsedSlide {
  const treeStart = xml.indexOf('<p:spTree');
  const from = treeStart < 0 ? 0 : treeStart;
  const to = xml.length;

  const shapes: SlideShape[] = [];
  let cursor = from;
  let truncated = false;

  while (cursor < to) {
    if (shapes.length >= options.maxShapes) { truncated = true; break; }

    const candidates = (['<p:sp', '<p:pic', '<p:graphicFrame', '<p:grpSp'] as const)
      .map((tag) => ({ tag, at: indexOfElement(xml, tag, cursor, to) }))
      .filter((c) => c.at >= 0)
      .sort((a, b) => a.at - b.at);
    const next = candidates[0];
    if (!next) break;

    const end = endOfElement(xml, next.at, next.tag.slice(1));
    if (next.tag === '<p:grpSp') {
      // Descend into groups rather than skipping them: a grouped title is
      // still a title, and PowerPoint groups liberally.
      cursor = xml.indexOf('>', next.at) + 1;
      continue;
    }

    const shape = parseShape(xml, next.at, end, options);
    if (shape) shapes.push(shape);
    cursor = end;
  }

  return { shapes, truncated };
}

/* ------------------------------------------------------------------ *
 * layouts and masters
 * ------------------------------------------------------------------ */

/**
 * Placeholder boxes declared by a layout or master.
 *
 * Read into a single table with the master's entries first, so a layout that
 * repositions a placeholder overwrites the master's version of it — which is
 * the inheritance order PresentationML specifies.
 */
export function readPlaceholderRects(
  xml: string, into: Map<string, SlideRect> = new Map(),
): Map<string, SlideRect> {
  const parsed = parseSlide(xml, {
    rels: new Map(), placeholderRects: new Map(), maxShapes: 512,
  });
  for (const shape of parsed.shapes) {
    if (shape.kind !== 'text' || !shape.rect) continue;
    const source = xml.slice(shape.start, shape.end);
    const phTag = /<p:ph\b([^>]*)/.exec(source)?.[1];
    if (!phTag) continue;
    into.set(placeholderKey(attr(phTag, 'type'), attr(phTag, 'idx')), shape.rect);
  }
  return into;
}

/** Slide dimensions in CSS pixels, from `ppt/presentation.xml`. */
export function readSlideSize(presentationXml: string): { width: number; height: number } {
  const size = /<p:sldSz\b[^>]*\bcx="(\d+)"[^>]*\bcy="(\d+)"/.exec(presentationXml);
  // 4:3 at 96 dpi, the value PowerPoint itself falls back to.
  if (!size) return { width: 960, height: 720 };
  return {
    width: Math.round(Number(size[1]) / EMU_PER_PX),
    height: Math.round(Number(size[2]) / EMU_PER_PX),
  };
}

/** Speaker notes text from a notes slide part (§17). */
export function readNotes(notesXml: string): string {
  const parsed = parseSlide(notesXml, {
    rels: new Map(), placeholderRects: new Map(), maxShapes: 64,
  });
  return parsed.shapes
    .filter((s) => s.kind === 'text' && s.placeholder !== 'sldNum')
    .map((s) => (s.kind === 'text' ? s.text : ''))
    .filter((t) => t.trim() !== '')
    .join('\n')
    .trim();
}

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

export interface ShapeEdit {
  /** Shape id as it appears in `<p:cNvPr id="…">`. */
  readonly id: string;
  readonly text: string;
}

export interface SlideEditResult {
  readonly xml: string;
  readonly dropped: readonly string[];
  readonly failed: readonly string[];
}

/**
 * Rewrite the text of specific shapes, leaving the rest of the slide alone.
 *
 * The shape's geometry, its `<a:bodyPr>`, its fill and its placeholder link
 * all sit outside `<p:txBody>`'s paragraph list and are copied verbatim. The
 * new text takes the first run's character properties and the first
 * paragraph's properties, which is the only honest collapse when several
 * differently-formatted runs become one string — and it is reported.
 */
export function applyShapeEdits(
  xml: string, shapes: readonly SlideShape[], edits: readonly ShapeEdit[],
): SlideEditResult {
  const dropped: string[] = [];
  const failed: string[] = [];
  const byId = new Map(shapes.map((s) => [s.id, s]));

  // Back to front, so each replacement's offsets are still valid.
  const ordered = [...edits].sort((a, b) => (byId.get(b.id)?.start ?? -1) - (byId.get(a.id)?.start ?? -1));

  let out = xml;
  for (const edit of ordered) {
    const shape = byId.get(edit.id);
    if (!shape || shape.kind !== 'text' || !shape.editable) { failed.push(edit.id); continue; }

    const source = out.slice(shape.start, shape.end);
    const bodyStart = source.indexOf('<p:txBody');
    if (bodyStart < 0) { failed.push(edit.id); continue; }
    const bodyOpenEnd = source.indexOf('>', bodyStart);
    const bodyClose = source.lastIndexOf('</p:txBody>');
    if (bodyOpenEnd < 0 || bodyClose < 0) { failed.push(edit.id); continue; }

    const body = source.slice(bodyOpenEnd + 1, bodyClose);
    const bodyPr = /<a:bodyPr\b[^>]*(?:\/>|>[\s\S]*?<\/a:bodyPr>)/.exec(body)?.[0] ?? '<a:bodyPr/>';
    const lstStyle = /<a:lstStyle\b[^>]*(?:\/>|>[\s\S]*?<\/a:lstStyle>)/.exec(body)?.[0] ?? '';
    const firstPPr = /<a:pPr\b[^>]*(?:\/>|>[\s\S]*?<\/a:pPr>)/.exec(body)?.[0] ?? '';
    const firstRPr = /<a:r\b[^>]*>[\s\S]*?(<a:rPr\b[^>]*(?:\/>|>[\s\S]*?<\/a:rPr>))/.exec(body)?.[1] ?? '';

    const formats = new Set(shape.paragraphs.flatMap((p) => p.runs)
      .map((r) => JSON.stringify([r.bold, r.italic, r.underline, r.sizePx, r.color])));
    if (formats.size > 1) {
      dropped.push(`도형 ${shape.name || shape.id}: 서로 다른 글자 서식이 첫 서식으로 통일됨`);
    }
    if (shape.paragraphs.some((p) => p.runs.some((r) => r.href !== null))) {
      dropped.push(`도형 ${shape.name || shape.id}: 하이퍼링크가 일반 텍스트로 바뀜`);
    }

    const paragraphs = edit.text.split('\n').map((line) => (
      line === ''
        ? `<a:p>${firstPPr}</a:p>`
        : `<a:p>${firstPPr}<a:r>${firstRPr}<a:t>${escapeXml(line)}</a:t></a:r></a:p>`
    )).join('');

    const rebuilt = source.slice(0, bodyOpenEnd + 1)
      + bodyPr + lstStyle + paragraphs
      + source.slice(bodyClose);
    out = out.slice(0, shape.start) + rebuilt + out.slice(shape.end);
  }

  return { xml: out, dropped, failed };
}
